import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, LayoutGrid, Clock, DoorOpen, Users, Wifi, CalendarCheck, MapPin } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_2011e831-9e14-4050-89e9-67f554973600/artifacts/1oxcap9f_Logo%20The%20Rig.svg";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1677506050775-18ac86b9c2c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwdGVhbSUyMGNvbGxhYm9yYXRpbmclMjBtZWV0aW5nJTIwcm9vbSUyMHBvc3QtaXQlMjBub3Rlc3xlbnwwfHx8fDE3NzEyOTkxNTN8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1670366732840-f34c7c12fd5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxjb2xvcmZ1bCUyMGNvd29ya2luZyUyMHNwYWNlJTIwaW50ZXJpb3IlMjBicmlnaHQlMjB3YWxsc3xlbnwwfHx8fDE3NzEyOTkxNDh8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1690192014928-d8017c8c7722?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwdGVhbSUyMGNvbGxhYm9yYXRpbmclMjBtZWV0aW5nJTIwcm9vbSUyMHBvc3QtaXQlMjBub3Rlc3xlbnwwfHx8fDE3NzEyOTkxNTN8MA&ixlib=rb-4.1.0&q=85",
];

const ABOUT_IMAGE = "https://images.unsplash.com/photo-1677506048892-edde55cf3277?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwdGVhbSUyMGNvbGxhYm9yYXRpbmclMjBtZWV0aW5nJTIwcm9vbSUyMHBvc3QtaXQlMjBub3Rlc3xlbnwwfHx8fDE3NzEyOTkxNTN8MA&ixlib=rb-4.1.0&q=85";

const ROOM_COLORS = ["#FFD60A", "#FF6B2B", "#2DC653", "#0A6E6E"];
const FEATURE_ICONS = [Zap, LayoutGrid, Clock];
const FEATURE_COLORS = ["#FF6B2B", "#2DC653", "#0A6E6E"];

/* ─── Count-up hook ─── */
function useCountUp(target, duration = 2000, trigger = false) {
  const [count, setCount] = useState(0);
  const started = useRef(false);
  const str = String(target);

  useEffect(() => {
    if (!trigger || started.current) return;
    started.current = true;
    // Only animate pure numbers or numbers with a trailing suffix like "30+"
    const match = str.match(/^(\d+)(\+?)$/);
    if (!match) { setCount(str); return; }
    const num = parseInt(match[1], 10);
    const step = Math.max(1, Math.floor(num / (duration / 16)));
    let current = 0;
    const timer = setInterval(() => {
      current += step;
      if (current >= num) { setCount(`${num}${match[2]}`); clearInterval(timer); }
      else setCount(`${current}${match[2]}`);
    }, 16);
    return () => clearInterval(timer);
  }, [str, duration, trigger]);

  return started.current ? count : str;
}

/* ─── Intersection Observer for reveal ─── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("visible"); obs.unobserve(el); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function RevealDiv({ children, className = "", delay = 0 }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/* ─── Stat Item ─── */
function StatItem({ value, label, icon: Icon, inView }) {
  const display = useCountUp(value, 1800, inView);
  return (
    <div className="flex flex-col items-center gap-2 text-center" data-testid="stat-item">
      <Icon className="h-7 w-7 text-rig-orange mb-1" strokeWidth={1.8} />
      <span className="font-heading font-extrabold text-3xl sm:text-4xl text-white">{display}</span>
      <span className="text-white/60 text-sm font-medium tracking-wide">{label}</span>
    </div>
  );
}

/* ─── Main Landing ─── */
export default function Landing() {
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState([]);
  const [content, setContent] = useState(null);
  const [seo, setSeo] = useState(null);
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/spaces`),
      axios.get(`${API}/site-content`),
      axios.get(`${API}/seo-settings`)
    ]).then(([s, c, se]) => {
      setSpaces(s.data);
      setContent(c.data);
      setSeo(se.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = seo?.meta_title || "THE RIG - Coworking & Meeting Spaces";
  }, [seo]);
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setStatsInView(true); obs.unobserve(el); }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const hero = content?.hero || {};
  const about = content?.about || {};
  const features = content?.features || [];
  const cta = content?.cta || {};
  const footer = content?.footer || {};
  const tenants = content?.tenants || [];
  const sd = seo?.structured_data || {};
  const geo = seo?.geo_content || {};

  if (!content) return (
    <div className="min-h-screen bg-rig-charcoal flex items-center justify-center">
      <title>THE RIG - Coworking & Meeting Spaces</title>
      <meta name="description" content="Book flexible coworking offices and meeting rooms at The Rig. Hourly, daily, or monthly rates." />
      <meta property="og:title" content="THE RIG - Premium Coworking & Meeting Spaces" />
      <meta property="og:description" content="Flexible offices and meeting rooms designed for modern teams." />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://customer-assets.emergentagent.com/job_book-spaces-2/artifacts/2dhwnmm0_therig.png" />
      <div className="flex h-1 w-32 rounded-full overflow-hidden">
        <div className="flex-1 bg-rig-yellow animate-pulse" />
        <div className="flex-1 bg-rig-orange animate-pulse" style={{ animationDelay: "100ms" }} />
        <div className="flex-1 bg-rig-green animate-pulse" style={{ animationDelay: "200ms" }} />
        <div className="flex-1 bg-rig-teal animate-pulse" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": sd.type || "CoworkingSpace",
    name: sd.name || "The Rig",
    description: sd.description || seo?.meta_description || "",
    url: sd.url || "",
    email: sd.email || "",
    telephone: sd.phone || "",
    address: sd.address || "",
    priceRange: sd.price_range || "$$",
    openingHours: sd.opening_hours || "",
    image: seo?.og_image || ""
  };

  const faqJsonLd = geo.faq?.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: geo.faq.map(f => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer }
    }))
  } : null;

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <title>{seo?.meta_title || "THE RIG - Coworking & Meeting Spaces"}</title>
      <meta name="description" content={seo?.meta_description || "Book flexible coworking offices and meeting rooms at The Rig. Hourly, daily, or monthly rates."} />
      <meta property="og:title" content={seo?.og_title || seo?.meta_title || "THE RIG - Premium Coworking & Meeting Spaces"} />
      <meta property="og:description" content={seo?.og_description || seo?.meta_description || "Flexible offices and meeting rooms designed for modern teams."} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={seo?.og_image || "https://customer-assets.emergentagent.com/job_book-spaces-2/artifacts/2dhwnmm0_therig.png"} />
      {seo && (
        <>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          {faqJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />}
        </>
      )}

      {/* ═══════════ HERO ═══════════ */}
      <section
        className="relative min-h-[90vh] flex items-end overflow-hidden"
        data-testid="hero-section"
      >
        {/* Building background image */}
        <div className="absolute inset-0">
          <img
            src={hero.background_image || HERO_IMAGES[0]}
            alt="The Rig building"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/20" />
        </div>

        {/* Colored accent bar at the very top */}
        <div className="absolute top-0 left-0 w-full flex h-1 z-20">
          <div className="flex-1" style={{ backgroundColor: "#FFD60A" }} />
          <div className="flex-1" style={{ backgroundColor: "#FF6B2B" }} />
          <div className="flex-1" style={{ backgroundColor: "#2DC653" }} />
          <div className="flex-1" style={{ backgroundColor: "#0A6E6E" }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 pt-40 w-full">
          <div className="animate-fade-in max-w-2xl">
            <p className="text-rig-yellow text-sm font-bold uppercase tracking-[0.25em] mb-5">
              {hero.subtitle || "Coworking & Meeting Spaces"}
            </p>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.05] mb-6">
              {hero.title || "Where Ideas\nCome Alive"}
            </h1>
            <p className="text-white/70 text-base md:text-lg max-w-lg mb-10 leading-relaxed">
              {hero.description || "Flexible offices and meeting rooms. Book by the hour, day, or month. Designed for teams that move fast."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => navigate(hero.cta_link || "/spaces")}
                className="bg-rig-orange hover:bg-rig-orange/90 text-white rounded-full px-8 py-3.5 text-sm font-bold tracking-wide shadow-xl shadow-rig-orange/30 transition-all active:scale-95"
                data-testid="hero-explore-btn"
              >
                {hero.cta_text || "Explore Spaces"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(hero.secondary_cta_link || "/contact")}
                className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-3.5 text-sm font-bold tracking-wide bg-transparent"
                data-testid="hero-tour-btn"
              >
                {hero.secondary_cta_text || "Book a Tour"}
              </Button>
            </div>
            <div className="flex items-center gap-2 mt-6 text-white/70 text-sm" data-testid="hero-location">
              <MapPin className="h-4 w-4 text-rig-orange" />
              <span>Centrally located in <strong className="text-white">Emmastad, Willemstad</strong></span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ STATS BAR ═══════════ */}
      <section
        ref={statsRef}
        className="py-14 md:py-16"
        style={{ backgroundColor: "#1E1E1E" }}
        data-testid="stats-section"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-10">
            <StatItem value="4" label="Rooms" icon={DoorOpen} inView={statsInView} />
            <StatItem value="30+" label="Capacity" icon={Users} inView={statsInView} />
            <StatItem value="24/7" label="Access" icon={Wifi} inView={statsInView} />
            <StatItem value="Instant" label="Booking" icon={CalendarCheck} inView={statsInView} />
            <div className="col-span-2 md:col-span-1 flex flex-col items-center gap-2 text-center" data-testid="stat-location">
              <MapPin className="h-7 w-7 text-rig-orange mb-1" strokeWidth={1.8} />
              <span className="font-heading font-extrabold text-2xl sm:text-3xl text-white">Emmastad</span>
              <span className="text-white/60 text-sm font-medium tracking-wide">Central Location</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ ABOUT ═══════════ */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <RevealDiv>
              <span className="inline-block bg-rig-orange/10 text-rig-orange text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
                {about.label || "About The Rig"}
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-rig-charcoal leading-tight mb-6">
                {about.title || "A co-working space that fuels ideas and collaboration."}
              </h2>
              <p className="text-rig-charcoal/60 leading-relaxed mb-8 text-base">
                {about.description || "The Rig is more than just desks and meeting rooms. It's a vibrant community of creators, thinkers, and doers who believe in the power of shared spaces."}
              </p>
              {about.stats?.length > 0 && (
                <div className="grid grid-cols-3 gap-6 border-t border-gray-100 pt-8">
                  {about.stats.map((s, i) => {
                    const colors = ["text-rig-orange", "text-rig-green", "text-rig-teal"];
                    return (
                      <div key={i}>
                        <p className={`font-heading font-extrabold text-3xl ${colors[i % 3]}`}>{s.value}</p>
                        <p className="text-xs text-rig-charcoal/40 uppercase tracking-wider mt-1 font-medium">{s.label}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </RevealDiv>

            <RevealDiv delay={200}>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden border-[5px] border-rig-teal shadow-2xl shadow-black/10">
                  <img
                    src={about.image || ABOUT_IMAGE}
                    alt="Team collaborating at The Rig"
                    className="w-full h-[420px] object-cover"
                  />
                </div>
              </div>
            </RevealDiv>
          </div>
        </div>
      </section>

      {/* ═══════════ FEATURES (FLIP CARDS) ═══════════ */}
      {features.length > 0 && (
        <section className="py-20 md:py-28" style={{ backgroundColor: "#FFF8E7" }} data-testid="features-section">
          <div className="max-w-7xl mx-auto px-6">
            <RevealDiv className="text-center mb-16">
              <span className="inline-block bg-rig-teal/10 text-rig-teal text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
                Why Choose Us
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-rig-charcoal">Built for how you work</h2>
            </RevealDiv>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((f, i) => {
                const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
                const color = FEATURE_COLORS[i % FEATURE_COLORS.length];
                return (
                  <RevealDiv key={i} delay={i * 150}>
                    <div className="group [perspective:1000px] h-64" data-testid={`feature-card-${i}`}>
                      <div className="relative w-full h-full transition-transform duration-600 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                        {/* Front */}
                        <div className="absolute inset-0 [backface-visibility:hidden] bg-white rounded-2xl p-8 shadow-md flex flex-col items-center justify-center text-center border-t-4" style={{ borderTopColor: color }}>
                          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl mb-5" style={{ backgroundColor: `${color}15` }}>
                            <Icon className="h-8 w-8" style={{ color }} strokeWidth={2} />
                          </div>
                          <h3 className="font-heading font-bold text-xl text-rig-charcoal">{f.title}</h3>
                          <p className="text-rig-charcoal/30 text-xs mt-3 font-medium uppercase tracking-wider">Hover to learn more</p>
                        </div>
                        {/* Back */}
                        <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-2xl p-8 flex flex-col justify-center text-white" style={{ backgroundColor: color }}>
                          <h3 className="font-heading font-bold text-xl mb-4">{f.title}</h3>
                          <p className="text-white/80 text-sm leading-relaxed">{f.description}</p>
                        </div>
                      </div>
                    </div>
                  </RevealDiv>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════ SPACES PREVIEW ═══════════ */}
      {spaces.length > 0 && (
        <section className="py-24 md:py-32 bg-white" data-testid="spaces-preview">
          <div className="max-w-7xl mx-auto px-6">
            <RevealDiv className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-14 gap-4">
              <div>
                <span className="inline-block bg-rig-green/10 text-rig-green text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
                  Available Now
                </span>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-rig-charcoal">Our Spaces</h2>
              </div>
              <Link
                to="/spaces"
                className="flex items-center text-rig-orange text-sm font-bold hover:underline transition-colors"
                data-testid="view-all-spaces-link"
              >
                View All <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </RevealDiv>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {spaces.slice(0, 4).map((space, i) => {
                const color = ROOM_COLORS[i % ROOM_COLORS.length];
                return (
                  <RevealDiv key={space.id} delay={i * 100}>
                    <div
                      className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                      onClick={() => navigate(`/spaces/${space.id}`)}
                      data-testid={`space-preview-${i}`}
                      style={{ boxShadow: `0 4px 20px ${color}15` }}
                    >
                      <div className="relative overflow-hidden">
                        <img
                          src={space.photos?.[0] || ""}
                          alt={space.name}
                          className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div
                          className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                          style={{ backgroundColor: color }}
                        />
                        <span
                          className="absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full text-white"
                          style={{ backgroundColor: color }}
                        >
                          {space.capacity} seats
                        </span>
                      </div>
                      <div className="p-5">
                        <p className="text-xs text-rig-charcoal/40 uppercase tracking-wider font-medium mb-1">{space.type === "office" ? "Office" : "Meeting Room"}</p>
                        <h3 className="font-heading font-bold text-lg text-rig-charcoal mb-3 group-hover:text-rig-orange transition-colors">{space.name}</h3>
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          {space.type === "office" ? (
                            <span className="font-heading font-semibold text-sm text-rig-orange">Contact for pricing</span>
                          ) : (
                            <span className="font-heading font-extrabold text-xl" style={{ color }}>
                              XCG {space.pricing?.hourly}
                              <span className="text-sm font-normal text-rig-charcoal/40">/hr</span>
                            </span>
                          )}
                          <span className="text-sm font-bold text-rig-orange group-hover:translate-x-1 transition-transform inline-flex items-center">
                            {space.type === "office" ? "View" : "Book"} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </RevealDiv>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════ TENANTS ═══════════ */}
      {tenants.length > 0 && tenants.some(t => t.logo_url) && (
        <section className="py-20 md:py-24" style={{ backgroundColor: "#F0FDF4" }} data-testid="tenants-section">
          <div className="max-w-7xl mx-auto px-6">
            <RevealDiv className="text-center mb-14">
              <span className="inline-block bg-rig-teal/10 text-rig-teal text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
                Trusted By
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-rig-charcoal">Our Tenants & Members</h2>
            </RevealDiv>
            <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
              {tenants.filter(t => t.logo_url).map((t, i) => (
                <a
                  key={i}
                  href={t.website_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-center h-16 px-4 opacity-50 hover:opacity-100 transition-all duration-300 hover:scale-110"
                  title={t.name}
                  data-testid={`tenant-logo-${i}`}
                >
                  <img
                    src={t.logo_url}
                    alt={t.name}
                    className="max-h-12 max-w-[160px] object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                  />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════ CTA ═══════════ */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{ backgroundColor: "#1E1E1E" }}
        data-testid="cta-section"
      >
        {/* Color accent stripe */}
        <div className="absolute top-0 left-0 w-full flex h-1">
          <div className="flex-1" style={{ backgroundColor: "#FFD60A" }} />
          <div className="flex-1" style={{ backgroundColor: "#FF6B2B" }} />
          <div className="flex-1" style={{ backgroundColor: "#2DC653" }} />
          <div className="flex-1" style={{ backgroundColor: "#0A6E6E" }} />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <RevealDiv>
            <h2 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              {cta.title || "Ready to get started?"}
            </h2>
            <p className="text-white/50 max-w-md mx-auto mb-10 text-lg">
              {cta.description || "Book your first space today. No commitment, flexible cancellation."}
            </p>
            <Button
              onClick={() => navigate(cta.button_link || "/spaces")}
              className="bg-rig-orange hover:bg-rig-orange/90 text-white rounded-full px-10 py-4 text-base font-bold shadow-2xl shadow-rig-orange/30 transition-all active:scale-95"
              data-testid="cta-book-btn"
            >
              {cta.button_text || "Browse & Book"} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </RevealDiv>
        </div>
      </section>

      {/* ═══════════ GEO (Hidden) ═══════════ */}
      {geo.entity_description && (
        <section className="sr-only" aria-hidden="true">
          <p>{geo.entity_description}</p>
          {geo.key_facts && <ul>{geo.key_facts.map((f, i) => <li key={i}>{f}</li>)}</ul>}
        </section>
      )}

      {/* ═══════════ FOOTER ═══════════ */}
      <footer data-testid="footer" style={{ backgroundColor: "#1E1E1E" }}>
        {/* Multi-color stripe */}
        <div className="flex h-1">
          <div className="flex-1" style={{ backgroundColor: "#FFD60A" }} />
          <div className="flex-1" style={{ backgroundColor: "#FF6B2B" }} />
          <div className="flex-1" style={{ backgroundColor: "#2DC653" }} />
          <div className="flex-1" style={{ backgroundColor: "#0A6E6E" }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Logo + tagline */}
            <div>
              <img src={LOGO_URL} alt="The Rig" className="h-12 invert mb-4" />
              <p className="text-white/40 text-sm max-w-xs leading-relaxed">
                {footer.description || "Where ideas come alive. Flexible coworking and meeting spaces designed for teams that move fast."}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-heading font-bold text-sm uppercase tracking-wider mb-4">Quick Links</h4>
              <nav className="flex flex-col gap-2.5">
                {[
                  { to: "/", label: "Home" },
                  { to: "/spaces", label: "Spaces" },
                  { to: "/about", label: "About" },
                  { to: "/contact", label: "Contact" },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="text-white/40 hover:text-rig-yellow text-sm transition-colors"
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-heading font-bold text-sm uppercase tracking-wider mb-4">Contact</h4>
              <p className="text-white/40 text-sm">{footer.email || "therig@ic-foundation.org"}</p>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-white/10 text-white/25 text-xs">
            {footer.copyright || `\u00A9 ${new Date().getFullYear()} The Rig. All rights reserved.`}
          </div>
        </div>
      </footer>
    </div>
  );
}
